/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ComponentFunction.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomDataGridV2 from "../../../common/datagrid/CustomDataGridV2";
import CustomSearchFilterV2 from "../../../common/searchBar/CustomSearchFilterV2";
// import CustomSearchFilterV2 from "../../../common/searchBar/CustomSearchFilterV2";
import CustomToolTip from "../../../../infoButton/CustomToolTip";

function ComponentFunction({
  type,
  componentFunction = [],
  searchKeys,
  gap = "2rem",
}) {
  const [filteredTableRows, setFilteredTableRows] = useState([]);
  const [filteredData, setFilteredData] = useState(componentFunction);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [slicedList, setSlicedList] = useState([]);

  // Update filteredData whenever componentFunction changes
  useEffect(() => {
    setFilteredData(componentFunction);
  }, [componentFunction]);

  useEffect(() => {
    let slice = filteredTableRows.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage,
    );

    setSlicedList(slice);
  }, [page, filteredTableRows, rowsPerPage]);

  // Function to convert filteredData into DataGrid rows
  const filterRowData = () => {
    const mappedData =
      filteredData?.map((item, index) => ({
        id: index + 1,
        column1: item,
      })) || [];
    setFilteredTableRows(mappedData);
  };

  // Run filterRowData whenever filteredData changes
  useEffect(() => {
    filterRowData();
  }, [filteredData]);

  // Columns for DataGrid - UPDATED WITH TOOLTIP 
  const COLUMN = [
    {
      field: "column1",
      headerName: "Column 1",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1;
        return (
          <Typography >
            {type === "EE" && (
              <CustomToolTip length={90}>
                {data?.description || ""}
              </CustomToolTip>
            )}
            {type === "SWM" &&
              `${data?.abbreviation || data?.abbrevation || ""} - ${data?.name || ""
              }`}
            {type === "VS" && (
              <CustomToolTip length={90}>
                {data?.description || ""}
              </CustomToolTip>
            )}
            {type === "VC" && (
              <CustomToolTip length={90}>
                {data?.description || ""}
              </CustomToolTip>
            )}
          </Typography>
        );
      },
    },
  ];

  const VS_VEHICLE_COMPONENT_COLUMNS = [
    {
      field: "column1",
      headerName: "Vehicle Component Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const fontSize = "14px";
        const fontColor = "#53565A";
        return (
          <Box sx={{ fontSize }}>
            <Typography
              sx={{ fontWeight: "bold", fontSize }}
            >{`Name: ${data?.name || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >{`Assembly Number: ${data?.component_number || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >
              Description:{" "}
              <CustomToolTip length={90}>
                {data?.description || "-"}
              </CustomToolTip>
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "column2",
      headerName: "Other Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const zoneDetails = Array.isArray(data?.zone_details)
          ? data.zone_details
            .map((zone) => zone?.zone_name)
            .filter(Boolean)
            .join(", ")
          : data?.zone_details?.zone_name;
        const suppliers = Array.isArray(data?.suppliers)
          ? data.suppliers.filter(Boolean).join(", ")
          : data?.suppliers;
        const fontSize = "14px";

        return (
          <Box>
            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Supplier:
              </Box>{" "}
              {suppliers || "-"}
            </Typography>
            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Zone Details:
              </Box>{" "}
              {zoneDetails || "-"}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const VC_EE_COMPONENT_COLUMNS = [
    {
      field: "column1",
      headerName: "EE Component Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const fontSize = "14px";
        const fontColor = "#53565A";
        return (
          <Box sx={{ fontSize }}>
            <Typography
              sx={{ fontWeight: "bold", fontSize }}
            >{`Assembly Number: ${data?.assembly_number || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >{`Version: ${data?.version || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >
              Description:{" "}
              <CustomToolTip length={90}>
                {data?.description || "-"}
              </CustomToolTip>
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "column2",
      headerName: "Other Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const zoneDetails = Array.isArray(data?.zone_details)
          ? data.zone_details
            .map((zone) => zone?.zone_name)
            .filter(Boolean)
            .join(", ")
          : data?.zone_details?.zone_name;
        const fontSize = "14px";

        return (
          <Box>
            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Supplier:
              </Box>{" "}
              {data?.supplier_name || "-"}
            </Typography>

            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Zone Details:
              </Box>{" "}
              {zoneDetails || "-"}
            </Typography>
          </Box>
        );
      },
    },
  ];

  if (type === "VS") {
    return (
      <Box
        sx={{
          width: "750px",
          display: "flex",
          flexDirection: "column",
          // gap: gap,
        }}
      >
        <Box mb={2}>
          <CustomSearchFilterV2
            placeholder="Search"
            searchKeys={searchKeys || []}
            getDataOnSearch={(filteredData) => {
              setFilteredData(filteredData);
              setPage(1);
            }}
            inputData={componentFunction}
          />
        </Box>

        <Box sx={{ height: "290px" }}>
          <CustomDataGridV2
            columns={VS_VEHICLE_COMPONENT_COLUMNS}
            rows={slicedList}
            hideHeadder={true}
            isMultiLine={true}
            showPagination={true}
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={filteredTableRows.length}
            handlePageChange={(pageNo) => {
              setPage(pageNo);
            }}
          />
        </Box>
      </Box>
    );
  }

  if (type === "VC") {
    return (
      <Box
        sx={{
          width: "750px",
          display: "flex",
          flexDirection: "column",
          gap: gap,
        }}
      >
        <Box mb={2}>
          <CustomSearchFilterV2
            placeholder="Search"
            searchKeys={searchKeys || []}
            getDataOnSearch={(filteredData) => {
              setFilteredData(filteredData);
              setPage(1);
            }}
            inputData={componentFunction}
          />
        </Box>

        <Box sx={{ height: "290px" }}>
          <CustomDataGridV2
            columns={VC_EE_COMPONENT_COLUMNS}
            rows={slicedList}
            hideHeadder={true}
            isMultiLine={true}
            showPagination={true}
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={filteredTableRows.length}
            handlePageChange={(pageNo) => {
              setPage(pageNo);
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "750px",
        display: "flex",
        flexDirection: "column",
        gap: gap,
      }}
    >
      {/* Search bar can be added here later */}
      <Box mb={2}>
        <CustomSearchFilterV2
          placeholder="Search"
          searchKeys={searchKeys || []}
          getDataOnSearch={(filteredData) => {
            setFilteredData(filteredData);
          }}
          inputData={componentFunction}
        />
      </Box>

      <Box sx={{ height: "270px" }}>
        <CustomDataGridV2
          columns={COLUMN}
          rows={filteredTableRows}
          hideHeader={true}
          page={page}
          rowsPerPage={rowsPerPage}
          showPagination={true}
          totalItems={filteredTableRows.length}
          handlePageChange={(pageNo) => {
            setPage(pageNo);
          }}
        />
      </Box>
    </Box>
  );
}

export default ComponentFunction;
